import 'client-only';

import type {
  SanityImageHotspot,
  SanityImageSource,
} from '@sanity/image-url/lib/types/types';
import isSanityImageObject from 'cms/utils/is-sanity-image-object';
import { SANITY_DATASET, SANITY_PROJECT_ID } from 'config/sanity-env';
import SanityClientContext from 'contexts/sanity-client';
import type { ImageLoader } from 'next/image';
import type { UseNextSanityImageProps } from 'next-sanity-image';
import { useNextSanityImage } from 'next-sanity-image';
import { useContext } from 'react';

const decimalToPercentage = (decimal: number): string => {
  const conversion = 100;
  return `${decimal * conversion}%`;
};

export interface UseImageResult {
  loader: ImageLoader;
  src: string;
  width: number;
  height: number;
  objectPosition?: string;
  hotspot?: SanityImageHotspot | null;
  blurData?: string | undefined;
}

export default function useImage(
  imageSource: SanityImageSource | null,
): UseImageResult | null {
  const sanityClient = useContext(SanityClientContext);
  const imageProps: UseNextSanityImageProps | null = useNextSanityImage(
    sanityClient ?? {
      clientConfig: {
        dataset: SANITY_DATASET,
        projectId: SANITY_PROJECT_ID,
      },
    },
    imageSource,
  );

  // ESLint is being stupid - this could be null
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (imageProps === null) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (isSanityImageObject(imageSource)) {
    return {
      ...imageProps,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      ...(imageSource.hotspot
        ? {
            hotspot: imageSource.hotspot,
            objectPosition: `${decimalToPercentage(
              imageSource.hotspot.x,
            )} ${decimalToPercentage(imageSource.hotspot.y)}`,
            blurData:
              'metadata' in imageSource
                ? imageSource.metadata.blurHash
                : undefined,
          }
        : undefined),
      blurData: imageSource.metadata?.lqip,
    };
  }

  return {
    ...imageProps,
  };
}
